import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import TimePicker from 'rc-time-picker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendarAlt, faClock, faUsers } from '@fortawesome/free-solid-svg-icons';
import { timeFormatUi, monthDayYearFullFormat } from '../../../../../constants/date';
import { minWorkersAmountToShowFilter } from '../../../../../constants/jobs';
import { resolveDateTime } from '../../../../../helpers/jobs';
import Validator from '../../../../common/forms/Validator';
import ErrorMessage from '../../../../common/forms/ErrorMessage';
import WorkerItemForSelection from '../../../../common/ui/WorkerItemForSelection';

import 'rc-time-picker/assets/index.css';

export default class CalendarPopup extends Validator {
  static propTypes = {
    jobData: PropTypes.shape({
      start: PropTypes.object.isRequired,
      startTime: PropTypes.object,
      endTime: PropTypes.object,
      hours: PropTypes.number,
      quantityOfWorkers: PropTypes.number,
      selectedWorkers: PropTypes.array
    }),
    workersList: PropTypes.array.isRequired,
    isLta: PropTypes.bool.isRequired,
    closePopup: PropTypes.func.isRequired,
    updateJobsListData: PropTypes.func.isRequired,
    deleteJobFromList: PropTypes.func.isRequired
  };

  static defaultProps = {
    messages: {
      maxLength: 'Only {1} worker(s) is/are needed for this shift.'
    }
  };

  constructor (props) {
    super(props);
    const { jobData, workersList, isLta } = props;
    const { id, startTime, endTime, hours, quantityOfWorkers, selectedWorkers } = jobData;
    this.state = {
      id: id || null,
      startTime: startTime || null,
      endTime: endTime || null,
      hours: hours || 0,
      quantityOfWorkers: quantityOfWorkers || '',
      selectedWorkers: selectedWorkers || [],
      searchValue: '',
      filteredWorkers: workersList || []
    };
    this.validationRules = {
      startTime: { required: true },
      endTime: { required: true },
      hours: { minJobHours: true, maxJobHours: true },
      quantityOfWorkers: { required: true }
    };
    if (isLta && quantityOfWorkers) {
      this.validationRules.selectedWorkers = { maxLength: workersList && workersList.length > 0 ? quantityOfWorkers : 0 };
    }
  }

  handleChangeStartTime = startTime => {
    this.handleChangeTime('startTime', startTime);
  };

  handleChangeEndTime = endTime => {
    this.handleChangeTime('endTime', endTime);
  };

  handleChangeTime = (name, value) => {
    if (!value) {
      this.setState({
        hours: 0
      });
    }
    this.setState({
      [name]: value
    }, () => { this.setDateTimeHours(); });
  };

  setDateTimeHours = () => {
    const { startTime, endTime } = this.state;
    const { start } = this.props.jobData;
    if (startTime && endTime) {
      const updatedData = resolveDateTime(start, startTime, endTime);
      this.setState({
        startTime: updatedData.startTime,
        endTime: updatedData.endTime,
        hours: updatedData.hours
      });
    }
  };

  handleChangeWorkersQuantity = event => {
    const { value } = event.target;
    const quantityOfWorkers = value === '' ? value : parseInt(value, 10);
    this.setState({ quantityOfWorkers });
    const { workersList, isLta } = this.props;
    if (!quantityOfWorkers) {
      this.validationRules.selectedWorkers = null;
    } else if (isLta && workersList && workersList.length > 0) {
      this.validationRules.selectedWorkers = { maxLength: quantityOfWorkers };
    }
  };

  _validate = () => {
    return this.validate(this.validationRules, this.state);
  };

  handleSubmit = e => {
    e.preventDefault();
    this._resetErrors();
    if (!this._validate()) {
      return;
    }
    const { id, startTime, endTime, hours, quantityOfWorkers, selectedWorkers } = this.state;
    const start = startTime.clone().startOf('day');
    // react-big-calendar requires start and end values for events. We couldn't use startTime and endTime values
    // because in case when job ends on the next day we will see event for few days.
    // But we need to see the start day of the job on calendar view
    // demo with multi-day events: http://intljusticemission.github.io/react-big-calendar/examples/index.html#basic
    this.props.updateJobsListData({
      id: id || _.uniqueId(),
      start: start,
      end: start,
      startTime,
      endTime,
      hours,
      quantityOfWorkers,
      selectedWorkers
    });
  };

  handleWorkerSelection = selectedWorker => {
    let { selectedWorkers } = this.state;
    const selectedWorkerIndex = selectedWorkers.findIndex(worker => worker.id === selectedWorker.id);
    if (selectedWorkerIndex < 0) {
      selectedWorkers.push(selectedWorker);
    } else {
      selectedWorkers.splice(selectedWorkerIndex, 1);
    }
    this.setState({ selectedWorkers });
  };

  selectAllWorkers = () => {
    const { filteredWorkers, selectedWorkers } = this.state;
    const allWorkersAreSelected = filteredWorkers.length === selectedWorkers.length;
    this.setState({ selectedWorkers: allWorkersAreSelected ? [] : _.cloneDeep(filteredWorkers) });
  };

  _mapWorkersById () {
    return this.state.selectedWorkers.map(worker => worker.id);
  };

  handleFilterWorker = e => {
    const { workersList } = this.props;
    const searchValue = e.target.value && e.target.value.toLowerCase();
    let filteredWorkers = workersList;
    if (searchValue) {
      filteredWorkers = workersList.filter(worker => {
        const fullName = `${worker.first_name} ${worker.last_name}`;
        return fullName.toLowerCase().includes(searchValue);
      });
    }
    this.setState({
      searchValue,
      filteredWorkers
    });
  };

  render () {
    const { closePopup, jobData: { start }, workersList, deleteJobFromList, isLta } = this.props;
    const { id, startTime, endTime, quantityOfWorkers, selectedWorkers, searchValue, filteredWorkers } = this.state;
    const selectedWorkersIds = this._mapWorkersById();
    const errors = this.validationErrors;
    const allWorkersAreSelected = filteredWorkers.length === selectedWorkers.length;
    const workersFilterIsShown = workersList.length >= minWorkersAmountToShowFilter;
    return (
      <div className='calendar-popup-wrapper'>
        <form
          className='popupMenu calendar-popup'
          onSubmit={this.handleSubmit}
        >
          <button
            type='button'
            className='non-button close-btn d-block'
            onClick={closePopup}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className='row align-items-center mb-4 mb-sm-3'>
            <label className='col-5 col-sm-3'>
              <span className='icon-wrapper'><FontAwesomeIcon icon={faCalendarAlt} /></span>
              Date
            </label>
            <div className='col-7 col-sm-9 d-flex align-items-center job-date'>
              { start.format(monthDayYearFullFormat) }
            </div>
          </div>
          <div className='row align-center mb-4 mb-sm-3'>
            <label className='col-12 col-sm-3 mb-1 mb-sm-0'>
              <span className='icon-wrapper'><FontAwesomeIcon icon={faClock} /></span>
              Time
            </label>
            <div className='col-12 col-sm-9 d-flex align-items-center'>
              <div className='input-picker-box'>
                <TimePicker
                  showSecond={false}
                  className='create-job-timepicker-wrapper'
                  placeholder='Select Time'
                  onChange={this.handleChangeStartTime}
                  format={timeFormatUi}
                  use12Hours
                  minuteStep={15}
                  value={startTime}
                  defaultOpenValue={moment().startOf('day')}
                />
              </div>
              <div className='input-picker-delimiter text-center'>to</div>
              <div className='input-picker-box right'>
                <TimePicker
                  showSecond={false}
                  className='create-job-timepicker-wrapper'
                  placeholder='Select Time'
                  onChange={this.handleChangeEndTime}
                  format={timeFormatUi}
                  use12Hours
                  minuteStep={15}
                  value={endTime}
                  defaultOpenValue={moment().startOf('day')}
                />
              </div>
            </div>
            { (errors.startTime || errors.endTime) && (
              <React.Fragment>
                <div className='col-6 offset-sm-3 col-sm-4 mt-1 pr-sm-0'>
                  <ErrorMessage errorText={errors.startTime} />
                </div>
                <div className='col-6 offset-sm-1 col-sm-4 mt-1 pl-sm-0'>
                  <ErrorMessage errorText={errors.endTime} />
                </div>
              </React.Fragment>
            )}
            { !errors.startTime && !errors.endTime && errors.hours && (
              <div className='col-6 offset-sm-3 col-sm-9 mt-1 text-right'>
                <ErrorMessage errorText={errors.hours} />
              </div>
            )}
          </div>
          <div className='row align-items-center mb-4 mb-sm-3'>
            <label className='col-12 col-sm-3'>
              <span className='icon-wrapper'><FontAwesomeIcon icon={faUsers} /></span>
              Quantity
            </label>
            <div className='col-12 col-sm-9 d-flex align-items-center'>
              <input
                className='input-picker'
                type='number'
                min='1'
                value={quantityOfWorkers}
                onChange={this.handleChangeWorkersQuantity}
              />
            </div>
            <div className='col-6 offset-sm-3 col-sm-9 mt-1'>
              <ErrorMessage errorText={errors.quantityOfWorkers} />
            </div>
          </div>
          { workersList && workersList.length > 0 && (
            <div className='mb-3'>
              <div className='d-flex flex-wrap'>
                { !isLta && <h4 className={`my-2 ${workersFilterIsShown ? 'w-100' : ''}`}>Favorites:</h4> }
                { workersFilterIsShown && (
                  <input
                    className='input lta-workers-filter'
                    placeholder={isLta ? 'Type worker name here' : 'Type your favorite worker name'}
                    value={searchValue}
                    onChange={this.handleFilterWorker}
                  />
                )}
                <button
                  type='button'
                  className='link-select-all-items'
                  onClick={this.selectAllWorkers}
                >{ allWorkersAreSelected ? 'Unselect All' : 'Select All' }</button>
              </div>
              <div className='user-predictions-modal-list'>
                { filteredWorkers.map(worker => (
                  <WorkerItemForSelection
                    key={worker.id}
                    worker={worker}
                    isSelected={selectedWorkersIds.includes(worker.id)}
                    handleWorkerSelection={this.handleWorkerSelection}
                  />
                ))}
              </div>
            </div>
          )}
          <ErrorMessage className='text-right mb-3' errorText={errors.selectedWorkers} />
          { id ? (
            <div className='d-flex flex-wrap justify-content-between'>
              <button type='button' className='button button-cancel' onClick={() => deleteJobFromList(id)}>Delete</button>
              <button type='submit' className='button button-primary'>Save</button>
            </div>
          ) : (
            <button
              className='button button-primary w-100'
              type='submit'
            >Add</button>
          )}
        </form>
      </div>

    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/createJob/createJob/LocationScheduleStep/CalendarPopup.js